import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, computed, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthStateService, Credentials } from './auth-state.service';

type AuthResponse = { data: Credentials; statusCode: number };

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _http = inject(HttpClient);
  private authState = inject(AuthStateService);
  private router = inject(Router);

  readonly scopeSignal = computed(() => this.authState.credentialsSignal()?.payload.role ?? '');

  get credentials(): Credentials | null {
    return this.authState.credentials;
  }

  constructor() {
    const queryParam: Record<string, string> = { ADMIN: 'admin', SUPER_ADMIN: 'super-admin', MODEL_ADMIN: 'model-admin' };

    // Redirect to the role-scoped login page when credentials transition from set to cleared.
    let previous = this.authState.credentialsSignal();
    effect(() => {
      const current = this.authState.credentialsSignal();
      if (previous !== null && current === null) {
        const lastScope = previous.payload.role;
        this.router.navigate([{ outlets: { dialog: null, primary: ['login', queryParam[lastScope] ?? 'admin'] } }]);
      }
      previous = current;
    });
  }

  loginAdmin(data: { email: string; password: string }): Observable<AuthResponse> {
    return this._http.post<AuthResponse>('/api/auth/admin/login', data, { observe: 'response' }).pipe(
      map((res: HttpResponse<AuthResponse>) => {
        const body = this.getResponseBody(res);
        this.authState.setCredentials(body.data);

        return body;
      }),
    );
  }

  loginSuperAdmin(data: { email: string; password: string }): Observable<AuthResponse> {
    return this._http.post<AuthResponse>('/api/auth/super-admin/login', data, { observe: 'response' }).pipe(
      map((res: HttpResponse<AuthResponse>) => {
        const body = this.getResponseBody(res);
        this.authState.setCredentials(body.data);

        return body;
      }),
    );
  }

  loginModelAdmin(data: { email: string; password: string }): Observable<AuthResponse> {
    return this._http.post<AuthResponse>('/api/auth/model-admin/login', data, { observe: 'response' }).pipe(
      map((res: HttpResponse<AuthResponse>) => {
        const body = this.getResponseBody(res);
        this.authState.setCredentials(body.data);

        return body;
      }),
    );
  }

  refreshToken(): Observable<AuthResponse> {
    return this._http.post<AuthResponse>('/api/auth/refresh-token', null, { observe: 'response' }).pipe(
      map((res: HttpResponse<AuthResponse>) => {
        const body = this.getResponseBody(res);
        this.authState.setCredentials(body.data);

        return body;
      }),
    );
  }

  logout() {
    this.authState.clearCredentials();
  }

  isLogged() {
    return !(this.authState.credentials === null);
  }

  isAdmin() {
    return this.authState.credentials?.payload.role === 'ADMIN';
  }

  isSuperAdmin() {
    return this.authState.credentials?.payload.role === 'SUPER_ADMIN';
  }

  isModelAdmin() {
    return this.authState.credentials?.payload.role === 'MODEL_ADMIN';
  }
  getId() {
    return this.authState.credentials?.payload.id;
  }

  requestPasswordReset(data: { email: string; role: string }): Observable<{ statusCode: number }> {
    return this._http
      .post<{ statusCode: number }>('/api/auth/request-reset-password', data, { observe: 'response' })
      .pipe(map((res: HttpResponse<{ statusCode: number }>) => ({ statusCode: res.body?.statusCode ?? res.status })));
  }

  resetPassword(data: { hash: string; newPassword: string }): Observable<{ statusCode: number; data: { role: string } }> {
    return this._http.put<{ data: { role: string }; statusCode: number }>('/api/auth/reset-password', data, { observe: 'response' }).pipe(
      map((res: HttpResponse<{ data: { role: string }; statusCode: number }>) => ({
        statusCode: res.body?.statusCode ?? res.status,
        data: res.body?.data ?? { role: '' },
      })),
    );
  }

  private getResponseBody(response: HttpResponse<AuthResponse>): AuthResponse {
    if (!response.body) {
      throw new Error('Empty authentication response body');
    }

    return response.body;
  }
}
